/* Moments Etched v15.4.5.2 — deterministic neck-label garment-color synchronization */
document.addEventListener('DOMContentLoaded', () => {
  const designer = document.querySelector('[data-me-designer]');
  if (!designer) return;

  const choices = Array.from(designer.querySelectorAll('[data-me-neck-label-choice]'));
  const customPlacement = designer.querySelector('[data-me-custom-neck-placement]');
  const property = designer.querySelector('[data-me-neck-label-property]');
  const help = designer.querySelector('[data-me-neck-label-help]');
  const helpOpen = designer.querySelector('[data-me-neck-label-help-open]');
  const helpClose = designer.querySelector('[data-me-neck-label-help-close]');
  let lastHelpTrigger = null;
  const livePreview = designer.querySelector('[data-me-live-preview]');
  const neckCanvas = designer.querySelector('[data-me-neck-preview-canvas]');
  const neckLogo = designer.querySelector('[data-me-neck-preview-logo]');
  const neckFiber = designer.querySelector('[data-me-neck-preview-fiber]');
  const neckOrigin = designer.querySelector('[data-me-neck-preview-origin]');
  const neckRn = designer.querySelector('[data-me-neck-preview-rn]');
  const neckCare = designer.querySelector('[data-me-neck-preview-care]');
  const neckSize = designer.querySelector('[data-me-neck-preview-size]');
  const variantDataNode = designer.querySelector('[data-me-neck-variant-data]');
  let neckVariantData = {};
  try { neckVariantData = JSON.parse(variantDataNode?.textContent || '{}'); } catch (error) { neckVariantData = {}; }

  function productNeckDefaults() {
    return {
      fiber: designer.dataset.meNeckFiberContent || 'Garment-specific fiber content',
      origin: designer.dataset.meNeckCountryOrigin || 'Country of origin set from garment',
      rn: designer.dataset.meNeckRnNumber || '',
      care: designer.dataset.meNeckCareLine || 'Machine wash cold · Wash inside out · Tumble dry low · Do not bleach · Do not iron decoration · Do not dry clean'
    };
  }

  function applyNeckProductData(data = {}, size = '') {
    const defaults = productNeckDefaults();
    const resolved = { ...defaults, ...data, care: defaults.care };
    if (neckFiber) neckFiber.textContent = resolved.fiber || defaults.fiber;
    if (neckOrigin) neckOrigin.textContent = resolved.origin || defaults.origin;
    if (neckCare) neckCare.textContent = resolved.care || defaults.care;
    if (neckRn) {
      const rnText = String(resolved.rn || '').trim();
      neckRn.textContent = rnText ? (rnText.toUpperCase().startsWith('RN') ? rnText : `RN ${rnText}`) : '';
      neckRn.hidden = !rnText;
    }
    if (neckSize) neckSize.textContent = size || 'SIZE';
  }

  function applyNeckFabricFromSelectedColor(colorName = '') {
    if (!neckCanvas) return false;

    const wanted = String(colorName || '').trim().toLowerCase();
    let swatch = null;
    const buttons = Array.from(designer.querySelectorAll('[data-me-designer-color]'));
    if (wanted) {
      swatch = buttons.find((button) => String(button.dataset.colorName || '').trim().toLowerCase() === wanted) || null;
    }
    swatch ||= designer.querySelector('[data-me-designer-color].is-active,[data-me-designer-color][aria-pressed="true"]');
    const circle = swatch?.querySelector('.me-designer-color-swatch__circle');
    if (!circle) return false;

    const computed = window.getComputedStyle(circle);
    const color = computed.backgroundColor;
    const image = computed.backgroundImage;
    const match = color && color.match(/rgba?\(\s*(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/i);

    // Solid-color swatches are authoritative for the inside-neck fabric tint. This
    // avoids relying on canvas pixel sampling, which can fail for Shopify CDN images
    // because of cross-origin restrictions. Gradient/image swatches (for example
    // Camo) fall back to the garment-image path below.
    if (!match || (image && image !== 'none')) return false;

    const r = Math.max(0, Math.min(255, Math.round(Number(match[1]))));
    const g = Math.max(0, Math.min(255, Math.round(Number(match[2]))));
    const b = Math.max(0, Math.min(255, Math.round(Number(match[3]))));
    neckCanvas.style.setProperty('--me-neck-fabric', `rgb(${r} ${g} ${b})`);

    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b);
    const ink = luminance < 145 ? '#ffffff' : '#181512';
    const line = luminance < 145 ? 'rgba(255,255,255,.82)' : 'rgba(24,21,18,.72)';
    const wash = luminance < 145 ? 'rgba(0,0,0,.08)' : 'rgba(255,255,255,.06)';
    neckCanvas.style.setProperty('--me-neck-ink', ink);
    neckCanvas.style.setProperty('--me-neck-line', line);
    neckCanvas.style.setProperty('--me-neck-wash', wash);
    neckCanvas.dataset.meNeckColorName = String(swatch.dataset.colorName || colorName || '');
    return true;
  }

  function syncNeckPreviewVisual(show) {
    if (!livePreview || !neckCanvas) return;
    livePreview.classList.toggle('is-neck-preview', Boolean(show));
    neckCanvas.hidden = !show;
    neckCanvas.setAttribute('aria-hidden', show ? 'false' : 'true');
    if (!show) return;
    // The selected color swatch is the authoritative fabric color. Do not
    // resample a shaded garment-photo pixel: that made bright colors render as
    // a darker/redder tint in the inside-neck preview. The neutral texture map
    // supplies fabric/collar shading without changing the selected hue.
    applyNeckFabricFromSelectedColor(designer.dataset.meSelectedColor || designer.dataset.meInitialColor || '');
    applyNeckProductData();
    if (neckLogo && !neckLogo.querySelector('img')) neckLogo.textContent = 'YOUR LOGO';
  }

  function labelFor(mode) {
    if (mode === 'me-branding') return 'Moments Etched Branding — $2 credit';
    if (mode === 'custom') return 'Your Custom Branding';
    return 'Keep Original Tag';
  }

  function sync() {
    const selected = choices.find((input) => input.checked) || choices[0];
    const mode = selected?.value || 'original';
    designer.dataset.meNeckLabelMode = mode;
    if (property) property.value = labelFor(mode);

    choices.forEach((input) => input.closest('.me-neck-label-choice__card')?.classList.toggle('is-selected', input === selected));

    const shouldUseCustomPlacement = mode === 'custom';
    const shouldShowNeckPreview = mode === 'custom' || mode === 'me-branding';
    syncNeckPreviewVisual(shouldShowNeckPreview);
    if (neckLogo && mode === 'me-branding') {
      neckLogo.innerHTML = '';
      const brandLogo = document.createElement('img');
      brandLogo.src = designer.dataset.meNeckBrandLogo || '';
      brandLogo.alt = 'Moments Etched';
      brandLogo.className = 'me-neck-preview-brand-logo';
      neckLogo.appendChild(brandLogo);
    } else if (neckLogo && mode === 'custom') {
      const customCard = designer.querySelector('[data-me-location-artwork="Custom Neck Label"]');
      const uploaded = customCard?.querySelector('.me-location-artwork__preview img');
      neckLogo.innerHTML = '';
      if (uploaded?.src) {
        const customLogo = document.createElement('img');
        customLogo.src = uploaded.src;
        customLogo.alt = 'Uploaded brand logo';
        neckLogo.appendChild(customLogo);
      } else {
        neckLogo.textContent = 'YOUR LOGO';
      }
    }
    if (customPlacement && customPlacement.checked !== shouldUseCustomPlacement) {
      customPlacement.checked = shouldUseCustomPlacement;
      customPlacement.dispatchEvent(new Event('change', { bubbles: true }));
      window.dispatchEvent(new CustomEvent('me:placements-updated', { detail: { source: 'neck-label' } }));
    }

    window.dispatchEvent(new CustomEvent('me:neck-label-updated', { detail: { mode, label: labelFor(mode) } }));

    // Choosing custom branding is also a preview action. Move the live preview
    // to the inside-neck view immediately so the customer gets visual feedback.
    if (mode === 'custom' || mode === 'me-branding') {
      window.setTimeout(() => {
        const card = designer.querySelector('[data-me-location-artwork="Custom Neck Label"]');
        card?.click();
        window.dispatchEvent(new CustomEvent('me:preview-placement', {
          detail: { placement: 'Custom Neck Label', artworkUrl: '' }
        }));
      }, 40);
    }
  }

  choices.forEach((input) => input.addEventListener('change', sync));
  window.addEventListener('me:preview-placement', (event) => {
    const placement = event.detail?.placement || '';
    // v15.4.4.1: preview visibility follows the ACTIVE preview placement only.
    // ME branding may initialize the neck preview, but it must never lock the
    // customer out of Left Chest / Full Back / other selected views.
    syncNeckPreviewVisual(placement === 'Custom Neck Label');
  });

  window.addEventListener('me:artwork-updated', () => {
    if (!neckLogo || (designer.dataset.meNeckLabelMode || 'original') !== 'custom') return;
    const card = designer.querySelector('[data-me-location-artwork="Custom Neck Label"]');
    const img = card?.querySelector('.me-location-artwork__preview img');
    neckLogo.innerHTML = '';
    if (img?.src) {
      const clone = document.createElement('img');
      clone.src = img.src;
      clone.alt = 'Uploaded brand logo';
      neckLogo.appendChild(clone);
    } else {
      neckLogo.textContent = 'YOUR LOGO';
    }
  });

  function refreshNeckFabricFromCurrentGarment() {
    // Re-assert the selected swatch after variant/media hydration. The swatch,
    // not a sampled product-image pixel, is the source of truth for neck color.
    const colorName = designer.dataset.meSelectedColor || designer.dataset.meInitialColor || '';
    window.requestAnimationFrame(() => applyNeckFabricFromSelectedColor(colorName));
  }

  window.addEventListener('me:designer-variant-changed', (event) => {
    const variantId = String(event.detail?.variantId || '');
    const size = String(event.detail?.size || '').trim();
    applyNeckProductData(neckVariantData[variantId] || {}, size);
    refreshNeckFabricFromCurrentGarment();
  });

  window.addEventListener('me:designer-color-changed', (event) => {
    // v15.4.5.2: update the neck fabric immediately from the actual selected swatch,
    // even while the customer is on Step 1 and even if the neck preview is hidden.
    // This makes the next neck-label view deterministic instead of depending on a
    // cross-origin canvas sample from the garment photo.
    const colorName = String(event.detail?.colorName || '').trim();
    if (colorName) designer.dataset.meSelectedColor = colorName;
    window.requestAnimationFrame(() => applyNeckFabricFromSelectedColor(colorName));
    window.setTimeout(() => applyNeckFabricFromSelectedColor(colorName), 60);
    window.setTimeout(refreshNeckFabricFromCurrentGarment, 90);
  });

  // Also catch direct preview-image src swaps from restore/edit flows that do not
  // emit a color event (for example cart re-edit hydration).
  const previewGarmentImage = designer.querySelector('[data-me-preview-product-image]');
  if (previewGarmentImage) {
    new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.attributeName === 'src' || mutation.attributeName === 'srcset')) {
        refreshNeckFabricFromCurrentGarment();
      }
    }).observe(previewGarmentImage, { attributes: true, attributeFilter: ['src', 'srcset'] });
  }

  applyNeckProductData();
  // Seed the neck fabric on first load from the active color swatch. This also
  // covers cart edit/restore before the customer ever opens the neck-label tab.
  window.requestAnimationFrame(() => {
    const active = designer.querySelector('[data-me-designer-color].is-active,[data-me-designer-color][aria-pressed="true"]');
    const initialColor = active?.dataset.colorName || designer.dataset.meInitialColor || '';
    if (initialColor) designer.dataset.meSelectedColor = initialColor;
    applyNeckFabricFromSelectedColor(initialColor);
  });

  function openHelp() {
    if (!help) return;
    lastHelpTrigger = document.activeElement;
    help.hidden = false;
    helpClose?.focus();
  }
  function closeHelp() {
    if (!help) return;
    help.hidden = true;
    lastHelpTrigger?.focus?.();
  }
  helpOpen?.addEventListener('click', openHelp);
  helpClose?.addEventListener('click', closeHelp);
  help?.addEventListener('click', (event) => { if (event.target === help) closeHelp(); });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && help && !help.hidden) { event.preventDefault(); closeHelp(); } });


  // v15.4.4.2 — Step 3 state reconciliation
  // Step transitions can occur after placement controls have already changed.
  // Re-read the checked controls when Artwork opens so every selected location
  // (Full Back, sleeves, etc.) is rebuilt instead of using a stale render.
  function reconcileArtworkStep() {
    if (String(designer.dataset.meStudioStage || '') !== '3') return;

    window.setTimeout(() => {
      const manager = window.MEArtworkManager;
      manager?.render?.();

      // Artwork Step should open on a real artwork placement, not remain stuck
      // on the informational Moments Etched inside-neck preview.
      const firstArtwork = Array.from(
        designer.querySelectorAll('[data-me-placement-checkbox]:checked')
      ).find((input) => input.value !== 'Custom Neck Label');

      if (firstArtwork?.value) {
        manager?.activate?.(firstArtwork.value);
      } else {
        syncNeckPreviewVisual(false);
      }
    }, 30);
  }

  const stageObserver = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.attributeName === 'data-me-studio-stage')) {
      reconcileArtworkStep();
    }
  });

  stageObserver.observe(designer, {
    attributes: true,
    attributeFilter: ['data-me-studio-stage']
  });


  sync();
  reconcileArtworkStep();
});
